import { type FC, useState, useEffect, useRef, useCallback } from "react";
import { useDemoMode } from "@/pages/demo/DemoModeContext";
import { Info, Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogDescription,
} from "@/shared/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { supabase } from "@/shared/lib/supabase";
import { uploadDocument } from "@/shared/utils/uploadDocument";
import papersIcon from "@/shared/assets/icons/papers-icon.svg";
import takePicture from "@/shared/assets/illustrations/take-picture.svg";
import { SecurityNote } from "@/shared/components/ui/security-note";
import { FileCard } from "@/shared/components/ui/file-card";
import { uploadZoneClasses } from "@/shared/components/ui/upload-zone";
import { ChevronRight } from "lucide-react";

const ACCEPTED_TYPES = ".pdf,.jpg,.jpeg,.png,.heic,.heif";
const ACCEPTED_LABEL = "PDF, PNG, JPG, or HEIC";

interface UploadDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (documentIds: number[]) => void;
  estateId: number;
  title?: string;
  subtitle?: string;
  tooltip?: string;
  securityNote?: string;
  documentType?: string;
  acceptedTypes?: string;
  acceptedLabel?: string;
  maxFiles?: number;
  onLimitExceeded?: (accepted: File[], blocked: File[]) => void;
  onTakePhoto?: () => void;
  size?: "sm" | "md" | "lg" | "xl" | "flow";
}

export const UploadDocumentModal: FC<UploadDocumentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  estateId,
  title = "Upload document",
  subtitle,
  tooltip,
  securityNote,
  documentType,
  acceptedTypes = ACCEPTED_TYPES,
  acceptedLabel = ACCEPTED_LABEL,
  maxFiles,
  onLimitExceeded,
  onTakePhoto,
  size = "md",
}) => {
  const { isDemoMode } = useDemoMode();
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
  });
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setFiles([]);
      setError(null);
      setIsDragging(false);
      setIsUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  }, [isOpen]);

  const handleClose = () => {
    if (isUploading) return;
    setFiles([]);
    setError(null);
    setIsDragging(false);
    onClose();
  };

  const addFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const fileArray = Array.from(newFiles);
      if (fileArray.length === 0) return;

      if (maxFiles !== undefined && onLimitExceeded) {
        const currentCount = files.length;
        if (currentCount + fileArray.length > maxFiles) {
          const all = [...files, ...fileArray];
          onLimitExceeded(all.slice(0, maxFiles), all.slice(maxFiles));
          return;
        }
      }

      setFiles((prev) => [...prev, ...fileArray]);
      setError(null);
    },
    [maxFiles, onLimitExceeded, files],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(e.target.files);
    }
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const handleSubmit = async () => {
    if (isDemoMode) {
      onClose();
      return;
    }
    if (files.length === 0 || isUploading) return;

    setIsUploading(true);
    setError(null);
    setUploadProgress({ current: 0, total: files.length });

    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      setError("Not authenticated");
      setIsUploading(false);
      return;
    }

    const failed: string[] = [];
    const documentIds: number[] = [];
    for (let i = 0; i < files.length; i++) {
      setUploadProgress({ current: i + 1, total: files.length });
      try {
        const docId = await uploadDocument(files[i], documentType || "other", estateId);
        documentIds.push(docId);
      } catch {
        failed.push(files[i].name);
      }
    }

    if (failed.length > 0) {
      setError(
        failed.length === files.length
          ? "Failed to upload. Please try again."
          : `Failed to upload: ${failed.join(", ")}`,
      );
      setFiles((prev) => prev.filter((f) => failed.includes(f.name)));
      setIsUploading(false);
    } else {
      setFiles([]);
      setIsUploading(false);
      onSuccess?.(documentIds);
      onClose();
    }
  };

  const handleAddMore = () => {
    inputRef.current?.click();
  };

  const emptyDropZone = (
    <>
      <label
        htmlFor="upload-document-input"
        className={uploadZoneClasses(isDragging)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <img src={papersIcon} alt="Upload documents" className="mb-3 h-14 w-14" />
        <p className="text-base font-medium">Choose a file</p>
        <p className="text-secondary">
          Click here to pick from your files, or drag it in ({acceptedLabel})
        </p>
      </label>
      {onTakePhoto && (
        <button
          onClick={() => {
            handleClose();
            onTakePhoto();
          }}
          className="flex items-center gap-4 rounded-lg border border-primary px-4 py-3 text-left hover:bg-subtle transition-colors"
        >
          <img src={takePicture} alt="Take a picture with phone" className="h-12 w-auto" />
          <span className="flex-1 text-sm font-medium">Take a picture with your phone</span>
          <ChevronRight className="h-4 w-4 text-secondary shrink-0" />
        </button>
      )}
    </>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent size={size}>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>{title}</DialogTitle>
            {tooltip && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 shrink-0 text-tertiary cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[240px]">{tooltip}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {subtitle && <DialogDescription className="my-3">{subtitle}</DialogDescription>}
        </DialogHeader>

        <DialogBody className="flex flex-col gap-4">
          <input
            id="upload-document-input"
            ref={inputRef}
            type="file"
            className="hidden"
            accept={acceptedTypes}
            multiple
            onChange={handleFileChange}
          />
          {isUploading ? (
            <div className="flex h-[200px] flex-col items-center justify-center rounded-lg border border-primary bg-white">
              <p className="font-medium">
                Uploading {uploadProgress.current} of {uploadProgress.total}
              </p>
              <Loader2 className="mt-3 h-6 w-6 animate-spin text-brand" />
            </div>
          ) : files.length > 0 ? (
            <>
              <div className="flex flex-col gap-2">
                {files.map((f, index) => (
                  <FileCard
                    key={`${f.name}-${f.size}-${index}`}
                    fileName={f.name}
                    fileSize={f.size}
                    onRemove={() => handleRemoveFile(index)}
                  />
                ))}
              </div>

              <Button variant="outline" onClick={handleAddMore}>
                + Add more files
              </Button>
            </>
          ) : (
            emptyDropZone
          )}

          {error && <FieldError message={error} />}

          {securityNote && <SecurityNote text={securityNote} />}

          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={handleClose} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={files.length === 0 || isUploading}
              variant="brand"
            >
              Continue
            </Button>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};
